import { Field, Stack, Text } from "@chakra-ui/react";
import { FORM_GAP, FormError, LoginNotice, LoginUI } from "./loginUI";
import type { UseLoginForm } from "./useLoginForm";

export function MagicLinkForm({
  form,
  ui,
  onUsePassword,
}: {
  form: UseLoginForm;
  ui: LoginUI;
  onUsePassword: () => void;
}) {
  const {
    email,
    isValid,
    error,
    linkError,
    isLoading,
    countdown,
    handleEmailChange,
    loginWithMagicLink,
  } = form;

  // A countdown is only active right after a magic link was sent, and it is
  // restored from cache on refresh — so it doubles as "magic link sent" state.
  const magicLinkSent = countdown > 0;

  return (
    // Every element of the form — banner, field, button, secondary action,
    // countdown — sits on the same rhythm: one gap, no per-element margins.
    <Stack
      width="full"
      as="form"
      gap={FORM_GAP}
      onSubmit={(e) => {
        e.preventDefault();
        loginWithMagicLink();
      }}
    >
      {/* One notice slot above the field: the stale-link explanation, or —
          once a link is on its way — the "check your inbox" card. */}
      {linkError && !magicLinkSent && (
        <LoginNotice title="That link didn't work">
          {linkError} Enter your email below and we'll send you a fresh one.
        </LoginNotice>
      )}

      {magicLinkSent && (
        <LoginNotice title="Magic link sent. Check your inbox 💌">
          Didn't receive the email? Check your spam folder or try again.
        </LoginNotice>
      )}

      {/* The field stays editable while the timer runs: a mistyped address
          can be corrected and sent to right away (the wait is per address). */}
      <Field.Root invalid={!isValid} gap={FORM_GAP}>
        {/* The field lays its children out flex-start, and the label itself
            is a flex row — so centring needs full width + justify, not
            text-align. */}
        <Field.Label
          width="100%"
          justifyContent="center"
          textAlign={"center"}
          color="whiteAlpha.700"
        >
          Enter your email to login
        </Field.Label>

        <ui.Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={handleEmailChange}
        />

        <FormError isValid={isValid} error={error} />
      </Field.Root>

      {/* While the retry timer runs the (disabled) button counts it down. */}
      <ui.SubmitButton
        isLoading={isLoading}
        disabled={isLoading || countdown > 0}
      >
        {countdown > 0 ? (
          // One span: the button is a flex container, so a separate number
          // span would become its own item with a gap before it.
          <Text as="span" fontVariantNumeric="tabular-nums">
            Try again in {countdown}s
          </Text>
        ) : (
          "Login"
        )}
      </ui.SubmitButton>

      {/* Always available: waiting for a link shouldn't block the other
          way in. */}
      <ui.GhostButton onClick={onUsePassword}>
        Login with password
      </ui.GhostButton>
    </Stack>
  );
}
